import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  Injectable,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { LoadScripts, DynamicScript } from '@fgb/portal-component-library/src/lib/shared/utilities';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { delay } from 'rxjs/operators';
import { WorksheetService } from '@fgb/core';
declare var YT: any;
declare var gtag: any;

@Injectable({ providedIn: 'root' })
export class YoutubeService {
  static behaviourSubject: BehaviorSubject<any>;

  constructor() {}

  addVideo(): Observable<any> {
    if (YoutubeService.behaviourSubject == null) {
      YoutubeService.behaviourSubject = new BehaviorSubject<any>('initializing');

      LoadScripts([{ src: 'https://www.youtube.com/iframe_api' }] as DynamicScript[]);

      if (!(window as any)['onYouTubeIframeAPIReady']) {
        (window as any)['onYouTubeIframeAPIReady'] = (e: any) => {
          let status = YoutubeService.behaviourSubject.value;
          if (status != 'loaded') {
            YoutubeService.behaviourSubject.next('loaded');
          }
        };
      } else {
        let status = YoutubeService.behaviourSubject.value;
        if (status != 'loaded') {
          YoutubeService.behaviourSubject.next('loaded');
        }
      }
    }

    return YoutubeService.behaviourSubject.asObservable();
  }
}

@Component({
  selector: 'fgbcl-youtube-player',
  standalone: true,
  imports: [],
  templateUrl: './youtube-player.component.html',
  styleUrls: ['./youtube-player.component.scss'],
})
export class YoutubePlayerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() streamKey: string;
  @Output() onStateChange = new EventEmitter();
  @ViewChild('container', { static: true }) container!: ElementRef;

  playerId = `yt-player-${crypto.randomUUID()}`;

  private subscription: Subscription;
  private player: any;
  private progressCheckInterval: any;
  private eightyPercentWatched = false;
  public static analyticsInterval = 0;

  constructor(
    private youtubeService: YoutubeService,
    private worksheetService: WorksheetService
  ) {}

  ngOnInit() {
    this.subscription = this.youtubeService
      .addVideo()
      .pipe(delay(100))
      .subscribe((youtubeStatus) => {
        if (youtubeStatus == 'loaded') {
          this.initializePlayer();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.streamKey && !changes.streamKey.firstChange) {
      this.updatePlayer();
    }
  }

  private updatePlayer() {
    if (this.player) {
      // Clear existing intervals
      this.clearIntervals();

      // Load new video if player exists
      this.player.loadVideoById(this.streamKey);
    } else {
      // If player doesn't exist yet, initialize it
      this.initializePlayer();
    }
  }

  private initializePlayer() {
    // Destroy existing player if it exists
    if (this.player && this.player.destroy) {
      this.player.destroy();
    }

    const width = this.container.nativeElement.offsetWidth;
    const height = Math.round(width * (9 / 16));
    this.player = new YT.Player(this.playerId, {
      width: width,
      height: height,
      videoId: this.streamKey,
      playerVars: {
        playsinline: 1,
        autoplay: 1,
      },
      events: {
        onStateChange: (event: any) => this.handleStateChange(event),
      },
    });
  }

  private handleStateChange(event: any) {
    this.onStateChange.emit(event);

    if (event.data == YT.PlayerState.PLAYING) {
      // Video started playing
      this.startProgressTracking();
      this.startAnalyticsInterval();

      gtag('event', 'streaming', {
        streamKey: this.streamKey,
        event_category: 'start_stream',
        event_label: this.streamKey,
      });
    } else if (event.data == YT.PlayerState.PAUSED) {
      // Video paused
      gtag('event', 'streaming', {
        streamKey: this.streamKey,
        event_category: 'pause_stream',
        event_label: this.streamKey,
      });
      this.clearIntervals();
    } else if (event.data == YT.PlayerState.ENDED) {
      // Video ended
      this.clearIntervals();
    }
  }

  private startProgressTracking() {
    this.eightyPercentWatched = false;
    this.progressCheckInterval = setInterval(() => {
      try {
        const duration = this.player.getDuration();
        const currentTime = this.player.getCurrentTime();
        const progress = (currentTime / duration) * 100;

        if (progress >= 80 && !this.eightyPercentWatched) {
          this.eightyPercentWatched = true;
          this.handleEightyPercentWatched();
        }
      } catch (err) {
        this.clearIntervals();
      }
    }, 1000); // Check every second
  }

  private handleEightyPercentWatched() {
    // Sending the POST request
    this.worksheetService.logStreamWatching(this.streamKey);

    gtag('event', 'streaming', {
      streamKey: this.streamKey,
      event_category: '80_percent_watched',
      event_label: this.streamKey,
    });
  }

  private startAnalyticsInterval() {
    YoutubePlayerComponent.analyticsInterval = window.setInterval(() => {
      gtag('event', 'streaming', {
        streamKey: this.streamKey,
        event_category: 'watching_stream',
        event_label: this.streamKey,
      });
    }, 30000);
  }

  private clearIntervals() {
    window.clearInterval(YoutubePlayerComponent.analyticsInterval);
    window.clearInterval(this.progressCheckInterval);
  }

  ngOnDestroy() {
    this.clearIntervals();
    this.subscription.unsubscribe();
    if (this.player && this.player.destroy) {
      this.player.destroy();
    }
  }
}
